/* eslint-disable react/style-prop-object */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Empty, Spin } from 'antd';
import {
   Chart as ChartJS,
   CategoryScale,
   LinearScale,
   BarElement,
   Title,
   Tooltip,
   Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import Utils from '../../../../misc/Utils';
import DiagnosticReportUtils from '../../../../misc/DiagnosticReportUtils';
import { IntroInd, InterpretationInd } from '../../diagnostic-components';

function getAssessmentId(assignmentId, fallbackAssessmentId) {
   return assignmentId !== undefined ? assignmentId : fallbackAssessmentId;
}

function getIndividualUrl(params, config) {
   const aid = getAssessmentId(params.assignment_id, params.assessment_id);
   const apiBase = process.env.REACT_APP_API_BRAINEE_DIAGNOSTIC;

   if (config.individualApiVersion === 3) {
      return `${apiBase}/v3/getdiagnosticreport?aid=${aid}&paid=${params.pre_assignment_id}&si=${params.student_id}`;
   }

   if (config.individualApiVersion === 2) {
      return `${apiBase}/v2/getdiagnosticreport?s=${params.subject_id}&ai=${aid}&si=${params.student_id}&school_year=${params.school_year}`;
   }

   return `${apiBase}/getdiagnosticreport?s=${params.subject_id}&ai=${aid}&si=${params.student_id}&t=${params.template}`;
}

export function SharedMTIndividualReport({ config, ...props }) {
   const [reportData, setReportData] = useState(null);
   const [reportPPData, setReportPPData] = useState({});
   const [dataLoading, setDataLoading] = useState(false);

   const {
      assignment_id,
      assessment_id,
      pre_assignment_id,
      student_id,
      subject_id,
      school_year,
      template,
      diagnostic_type
   } = props;

   useEffect(() => {
      Utils.verifyJwt();
      ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

      setDataLoading(true);
      const requestParams = {
         assignment_id,
         assessment_id,
         pre_assignment_id,
         student_id,
         subject_id,
         school_year,
         template,
         diagnostic_type
      };

      axios.get(getIndividualUrl(requestParams, config)).then((response) => {
         if (response.data && response.data[0]) {
            const selectedData = response.data.find((item) => item.diagnostic_type === props.diagnostic_type) || response.data[0];
            setReportData(selectedData);

            setReportPPData({
               Pre: response.data.filter((item) => item.diagnostic_type === 'pre'),
               Post: response.data.filter((item) => item.diagnostic_type === 'post'),
            });
         }
         setDataLoading(false);
      }, () => setDataLoading(false));
   }, [assignment_id, assessment_id, pre_assignment_id, student_id, subject_id, school_year, template, config]);

   const areaScores = useMemo(() => {
      if (!reportData) return [];
      return config.summaryAreas.map((area) => ({
         ...area,
         score: DiagnosticReportUtils.sumDataRange(reportData, area.count, area.start),
      }));
   }, [reportData, config.summaryAreas]);

   const totalScore = useMemo(() => areaScores.reduce((sum, area) => sum + area.score, 0), [areaScores]);

   const graphData = useMemo(() => ({
      labels: areaScores.map((x) => x.label),
      datasets: [{
         label: 'Percentage',
         backgroundColor: '#4F81BD',
         borderColor: '#4F81BD',
         data: areaScores.map((x) => (!isNaN((x.score / x.count) * 100) ? ((x.score / x.count) * 100).toFixed(2) : 0)),
      }],
   }), [areaScores]);

   const graphOptions = {
      responsive: true,
      plugins: {
         legend: { position: 'top' },
         title: { display: true, text: 'Percent Correct for the Content Standards' },
      },
      scales: { y: { min: 0, max: 100, stepSize: 1 }, x: {} },
   };

   if (dataLoading) {
      return <div className='center-empty-space'><Spin size='large' tip='Generating report, please wait...' /></div>;
   }

   if (!reportData) {
      return <div className='center-empty-space'><Empty description='No Data Found' image='../images/brainee_mascot.png' /></div>;
   }

   return (
      <div className='dashboard-item-inner3' style={{ padding: '7px' }}>
         <div style={{ fontFamily: 'calibri' }} id='individual-report'>
            <IntroInd
               subject={props.subject}
               grade_level={reportData.grade_level}
               student_name={reportData.student_name}
               school_name={reportData.school_name}
               section={reportData.section}
               diagnostic_date={reportData.diagnostic_date}
            />

            <p style={{ marginTop: '0pt', marginBottom: '0pt', lineHeight: 'normal', fontSize: '10pt' }}><strong>&nbsp;</strong></p>
            <p style={{ marginTop: '0pt', marginBottom: '0pt', lineHeight: 'normal', fontSize: '12pt' }}><strong>Competency Score</strong></p>
            <div style={{ textAlign: 'center' }}>
               <table cellSpacing={0} cellPadding={0} style={{ marginRight: 'auto', marginLeft: 'auto', border: '0.75pt solid #cccccc', borderCollapse: 'collapse', width: '100%' }}>
                  <tbody>
                     <tr>
                        <td style={{ width: '12.6pt', borderStyle: 'solid', borderWidth: '0.75pt', paddingRight: '5.4pt', paddingLeft: '4.9pt', verticalAlign: 'middle', backgroundColor: '#4F81BD' }}><p style={{ marginTop: '0pt', marginBottom: '0pt', fontSize: '11pt' }}><strong><span style={{ color: '#ffffff' }}>&nbsp;</span></strong></p></td>
                        <td style={{ width: '382.3pt', borderStyle: 'solid', borderWidth: '0.75pt', paddingRight: '5.4pt', paddingLeft: '5.4pt', verticalAlign: 'middle', backgroundColor: '#4F81BD' }}><p style={{ marginTop: '0pt', marginBottom: '0pt', fontSize: '11pt' }}><strong><span style={{ color: '#ffffff' }}>Learning Competency</span></strong></p></td>
                        <td style={{ width: '31.7pt', borderStyle: 'solid', borderWidth: '0.75pt', paddingRight: '5.4pt', paddingLeft: '5.4pt', verticalAlign: 'middle', backgroundColor: '#4F81BD' }}><p style={{ marginTop: '0pt', marginBottom: '0pt', textAlign: 'center', fontSize: '11pt' }}><strong><span style={{ color: '#ffffff' }}>Result</span></strong></p></td>
                        <td style={{ width: '81pt', borderStyle: 'solid', borderWidth: '0.75pt', paddingRight: '4.9pt', paddingLeft: '5.4pt', verticalAlign: 'middle', backgroundColor: '#4F81BD' }}><p style={{ marginTop: '0pt', marginBottom: '0pt', textAlign: 'center', fontSize: '11pt' }}><strong><span style={{ color: '#ffffff' }}>Pretest Percent Correct for the Level</span></strong></p></td>
                        <td style={{ width: '81pt', borderStyle: 'solid', borderWidth: '0.75pt', paddingRight: '4.9pt', paddingLeft: '5.4pt', verticalAlign: 'middle', backgroundColor: '#4F81BD' }}><p style={{ marginTop: '0pt', marginBottom: '0pt', textAlign: 'center', fontSize: '11pt' }}><strong><span style={{ color: '#ffffff' }}>Posttest Percent Correct for the Level</span></strong></p></td>
                     </tr>

                     {config.sections.map((section) => (
                        <React.Fragment key={section.label}>
                           {DiagnosticReportUtils.renderPartLabel(section.label)}
                           {Array.from({ length: section.end - section.start + 1 }, (_, o) => {
                              const index = section.start + o;
                              const competency = config.competencies.find((x) => x.index === index);
                              return (
                                 <React.Fragment key={`mt-ind-${index}`}>
                                    {DiagnosticReportUtils.renderCompetencyRowV2(
                                       competency ? competency.label : '',
                                       reportData,
                                       reportPPData,
                                       index
                                    )}
                                 </React.Fragment>
                              );
                           })}
                        </React.Fragment>
                     ))}
                  </tbody>
               </table>
            </div>

            <p style={{ marginTop: '0pt', marginBottom: '0pt', lineHeight: 'normal' }}><strong>&nbsp;</strong></p>
            <p style={{ marginTop: '0pt', marginBottom: '0pt', lineHeight: 'normal', fontSize: '10pt' }}><span><strong>Summary of Results.</strong>&nbsp; The result for the content standards is expressed in terms of the raw score, percent correct, standard score, and proficiency level.&nbsp;</span></p>

            <div style={{ marginTop: '20px', paddingLeft: '120px', paddingRight: '120px' }}>
               <Bar options={graphOptions} data={graphData} />
            </div>

            <div style={{ pageBreakBefore: 'always' }}></div>
            <p style={{ marginTop: '0pt', marginBottom: '0pt', lineHeight: 'normal' }}><strong>&nbsp;</strong></p>
            <p style={{ marginTop: '0pt', marginBottom: '0pt', lineHeight: 'normal', fontSize: '12pt' }}><strong>Summary of Results</strong></p>
            <table cellSpacing={0} cellPadding={0} style={{ border: '0.75pt solid #cccccc', borderCollapse: 'collapse', width: '100%' }}>
               <tbody>
                  <tr>
                     <td style={{ width: '197.1pt', borderStyle: 'solid', borderWidth: '0.75pt', paddingRight: '5.4pt', paddingLeft: '4.9pt', verticalAlign: 'middle', backgroundColor: '#4F81BD' }}><p style={{ marginTop: '0pt', marginBottom: '0pt', fontSize: '11pt' }}><strong><span style={{ color: '#ffffff' }}>Areas</span></strong></p></td>
                     <td style={{ width: '79.2pt', borderStyle: 'solid', borderWidth: '0.75pt', paddingRight: '5.4pt', paddingLeft: '5.4pt', verticalAlign: 'middle', backgroundColor: '#4F81BD' }}><p style={{ marginTop: '0pt', marginBottom: '0pt', textAlign: 'center', fontSize: '11pt' }}><strong><span style={{ color: '#ffffff' }}>Raw Score</span></strong></p></td>
                     <td style={{ width: '61.2pt', borderStyle: 'solid', borderWidth: '0.75pt', paddingRight: '5.4pt', paddingLeft: '5.4pt', verticalAlign: 'middle', backgroundColor: '#4F81BD' }}><p style={{ marginTop: '0pt', marginBottom: '0pt', textAlign: 'center', fontSize: '11pt' }}><strong><span style={{ color: '#ffffff' }}>Percent Correct</span></strong></p></td>
                     <td style={{ width: '59.9pt', borderStyle: 'solid', borderWidth: '0.75pt', paddingRight: '5.4pt', paddingLeft: '5.4pt', verticalAlign: 'middle', backgroundColor: '#4F81BD' }}><p style={{ marginTop: '0pt', marginBottom: '0pt', textAlign: 'center', fontSize: '11pt' }}><strong><span style={{ color: '#ffffff' }}>Standard Score</span></strong></p></td>
                     <td style={{ width: '99.4pt', borderStyle: 'solid', borderWidth: '0.75pt', paddingRight: '4.9pt', borderLeftStyle: 'solid', borderLeftWidth: '1pt', paddingLeft: '5.4pt', verticalAlign: 'middle', backgroundColor: '#4F81BD' }}><p style={{ marginTop: '0pt', marginBottom: '0pt', textAlign: 'center', fontSize: '11pt' }}><strong><span style={{ color: '#ffffff' }}>Proficiency Level</span></strong></p></td>
                  </tr>
                  {areaScores.map((area) => (
                     <React.Fragment key={`sum-${area.key}`}>
                        {DiagnosticReportUtils.renderSummaryRow(area.label, area.score, area.count, area.mean, area.stdDev)}
                     </React.Fragment>
                  ))}
                  {DiagnosticReportUtils.renderSummaryRow('Total', totalScore, config.totalSummary.count, config.totalSummary.mean, config.totalSummary.stdDev)}
               </tbody>
            </table>

            <InterpretationInd backgroundColor='#4F81BD' />

            <p style={{ marginTop: '0pt', marginBottom: '10pt' }}>&nbsp;</p>
            <p style={{ marginTop: '0pt', marginBottom: '10pt' }}><strong>Name:&nbsp;</strong>{reportData.student_name}</p>
         </div>
      </div>
   );
}
